<template>
  <div class="home">
    <section class="hero" aria-label="Black Hills Mountain Lion Foundation">
      <div class="hero__media" aria-hidden="true" />
      <div class="hero__veil" aria-hidden="true" />
      <div class="bh-container hero__content">
        <p class="hero__brand bh-reveal">Black Hills Mountain Lion Foundation</p>
        <h1 class="hero__title bh-reveal bh-reveal-delay-1">
          Conserving mountain lions through education and responsible hunting
        </h1>
        <p class="hero__lead bh-reveal bh-reveal-delay-2">
          A South Dakota 501(c)(3) nonprofit supporting hunter education,
          science-based wildlife management, and the next generation of outdoor
          stewards.
        </p>
        <div class="hero__actions bh-reveal bh-reveal-delay-3">
          <DonateButton variant="flat" color="primary" text="Donate" />
          <router-link class="bh-btn bh-btn--ghost" to="/events">
            View events
          </router-link>
        </div>
      </div>
    </section>

    <section class="bh-section mission">
      <div v-reveal class="bh-container mission__inner">
        <h2 class="bh-section__title">Our mission</h2>
        <p class="mission__copy">
          The Black Hills Mountain Lion Foundation educates hunters of all ages,
          promotes responsible hunting practices, and helps conserve mountain
          lions of the Black Hills. We support the
          <router-link class="mission__link" to="/wildlife-model">
            North American Model of Wildlife Conservation
          </router-link>, emphasizing science-based management and sustainable
          use of wildlife for future generations.
        </p>
      </div>
    </section>

    <UpcomingEvents />

    <section class="bh-section sponsor-band">
      <div v-reveal class="bh-container sponsor-band__inner">
        <h2 class="bh-section__title">Partner with us</h2>
        <p class="bh-section__lead">
          Sponsorships and gifts fund education programs, youth hunting
          partnerships, and our annual conservation banquet.
        </p>
        <div class="sponsor-band__actions">
          <router-link class="bh-btn bh-btn--secondary" to="/sponsor">
            Become a sponsor
          </router-link>
          <DonateButton
            variant="flat"
            color="primary"
            text="Donate with Venmo"
          />
        </div>
      </div>
    </section>

    <section class="bh-section impact">
      <div v-reveal="{ variant: 'scale' }" class="bh-container impact__inner">
        <h2 class="bh-section__title">Community impact</h2>
        <p class="bh-section__lead">
          Through our annual banquet, we support youth hunter education across
          the Black Hills.
        </p>
        <a
          class="impact__partner"
          href="https://sdyouthhunt.com/"
          target="_blank"
          rel="noopener noreferrer"
          title="Visit South Dakota Youth Hunting Adventures"
        >
          <img
            :src="require('@/assets/sdyha.png')"
            alt="South Dakota Youth Hunting Adventures"
            width="280"
          />
        </a>
      </div>
    </section>
  </div>
</template>

<script>
import DonateButton from "../components/DonateButton.vue";
import UpcomingEvents from "../components/UpcomingEvents.vue";

export default {
  name: "HomePage",
  components: {
    DonateButton,
    UpcomingEvents,
  },
};
</script>

<style scoped>
.home {
  text-align: center;
}

.hero {
  position: relative;
  min-height: min(88vh, 760px);
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  overflow: hidden;
  color: #fff;
}

.hero__media {
  position: absolute;
  inset: 0;
  background-image: url("~@/assets/cougar-rock.jpg");
  background-position: left 30% center;
  background-size: cover;
  background-repeat: no-repeat;
  transform: scale(1.04);
  animation: bh-fade-in 1.2s ease both;
}

.hero__veil {
  position: absolute;
  inset: 0;
  background: linear-gradient(
      180deg,
      rgba(20, 32, 28, 0.2) 0%,
      rgba(20, 32, 28, 0.35) 40%,
      rgba(20, 32, 28, 0.55) 100%
    ),
    linear-gradient(
      90deg,
      transparent 0%,
      transparent 38%,
      rgba(20, 32, 28, 0.45) 62%,
      rgba(20, 32, 28, 0.78) 100%
    );
}

.hero__content {
  position: relative;
  z-index: 1;
  padding: 6rem 0 4.5rem;
  text-align: left;
  max-width: 44rem;
  margin-left: auto;
  margin-right: max(1.25rem, calc((100% - var(--bh-container)) / 2));
  width: min(100% - 2.5rem, 40rem);
}

.hero__brand {
  font-family: var(--bh-font-display);
  font-size: clamp(1.35rem, 2.4vw, 1.85rem);
  font-weight: 700;
  letter-spacing: -0.02em;
  margin: 0 0 1rem;
  color: #fff;
}

.hero__title {
  font-family: var(--bh-font-display);
  font-size: clamp(1.35rem, 2.6vw, 1.85rem);
  font-weight: 500;
  line-height: 1.35;
  margin: 0 0 1rem;
  color: rgba(255, 255, 255, 0.92);
  max-width: 28ch;
}

.hero__lead {
  font-size: 1.05rem;
  line-height: 1.55;
  color: rgba(255, 255, 255, 0.82);
  margin: 0 0 1.75rem;
  max-width: 36rem;
}

.hero__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.mission {
  background: var(--bh-surface);
}

.mission__inner {
  max-width: 42rem;
}

.mission__copy {
  font-size: 1.125rem;
  line-height: 1.7;
  color: var(--bh-ink);
  margin: 0;
}

.mission__link {
  color: var(--bh-forest);
  font-weight: 600;
  text-underline-offset: 2px;
}

.mission__link:hover {
  color: var(--bh-focus);
}

.sponsor-band {
  background: linear-gradient(
    160deg,
    rgba(30, 58, 47, 0.06),
    rgba(243, 245, 242, 1) 55%
  );
}

.sponsor-band__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: center;
}

.impact {
  background: var(--bh-surface);
  border-top: 1px solid var(--bh-border);
}

.impact__partner {
  display: inline-block;
  padding: 1.25rem 1.5rem;
  border-radius: var(--bh-radius);
  border: 1px solid var(--bh-border);
  background: #fff;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}

.impact__partner:hover {
  box-shadow: var(--bh-shadow);
  transform: translateY(-2px);
}

.impact__partner img {
  display: block;
  max-width: 280px;
  height: auto;
}

@media (max-width: 720px) {
  .hero {
    min-height: 78vh;
    align-items: flex-end;
    justify-content: center;
  }

  .hero__media {
    background-position: 20% center;
  }

  .hero__veil {
    background: linear-gradient(
        180deg,
        rgba(20, 32, 28, 0.15) 0%,
        rgba(20, 32, 28, 0.35) 40%,
        rgba(20, 32, 28, 0.88) 100%
      ),
      linear-gradient(
        90deg,
        transparent 0%,
        rgba(20, 32, 28, 0.25) 55%,
        rgba(20, 32, 28, 0.55) 100%
      );
  }

  .hero__content {
    margin-left: auto;
    margin-right: auto;
    text-align: left;
    padding-bottom: 3rem;
    width: min(100% - 2.5rem, 40rem);
  }
}
</style>
